import React, { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';

// Location coordinates
const locationCoordinates = {
  'frankfurt-schwanheim': { lat: 50.0753, lng: 8.6114, name: 'Frankfurt Schwanheim' },
  'frankfurt-niederrad': { lat: 50.0833, lng: 8.6333, name: 'Frankfurt Niederrad' },
  'offenbach': { lat: 50.1047, lng: 8.7625, name: 'Offenbach am Main' },
  'bad-homburg': { lat: 50.2268, lng: 8.6183, name: 'Bad Homburg' },
};

const LocationMap = ({ slug, address, name }) => {
  const coords = locationCoordinates[slug];
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient || !coords || mapInstanceRef.current) return;

    // Dynamic import to avoid SSR issues
    const initMap = async () => {
      const L = (await import('leaflet')).default;
      await import('leaflet/dist/leaflet.css');

      // Fix default marker icon
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
      });

      if (mapRef.current && !mapInstanceRef.current) {
        const map = L.map(mapRef.current, {
          scrollWheelZoom: false,
        }).setView([coords.lat, coords.lng], 15);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        }).addTo(map);

        const marker = L.marker([coords.lat, coords.lng]).addTo(map);
        marker.bindPopup(`
          <div style="text-align: center;">
            <strong style="display: block; color: #1e293b;">Fahrschule Rößner</strong>
            <span style="color: #64748b; font-size: 0.875rem;">${name}</span>
            <br />
            <a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}"
               target="_blank" rel="noopener noreferrer"
               style="color: #2563eb; font-size: 0.875rem;">
              Route planen
            </a>
          </div>
        `);

        mapInstanceRef.current = map;
      }
    };

    initMap();

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [isClient, coords, name, address]);
  
  if (!coords) {
    // Fallback to a nice placeholder if coordinates not found
    return (
      <div className="w-full h-[300px] bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl flex flex-col items-center justify-center text-white">
        <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mb-4">
          <MapPin className="w-8 h-8 text-blue-400" />
        </div>
        <p className="font-semibold text-lg">{name}</p>
        <p className="text-slate-400 text-sm mt-1">{address}</p>
        <a 
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-full text-sm font-medium transition-colors"
        >
          In Google Maps öffnen
        </a>
      </div>
    );
  }

  return (
    <div 
      ref={mapRef}
      className="w-full h-[300px] rounded-xl overflow-hidden"
      style={{ minHeight: '300px' }}
    />
  );
};

export default LocationMap;
